import "@shoelace-style/shoelace/dist/components/animation/animation.js";
import "@shoelace-style/shoelace/dist/components/icon-button/icon-button.js";

import { html } from "lit";
import { customElement, query, state } from "lit/decorators.js";
import { BaseElement } from "../BaseElement";
import { styles } from "./Navigator.style";
import { NavigationDirectionChangedEvent } from "../../NavigatorService";

import "../Compass/Compass";
import "../Distance/Distance";

import { getGradientColor } from "./Navigator.gradient";

const ANIM_SPEED_MIN = 0.5;
const ANIM_SPEED_MAX = 4;

@customElement("ds-navigator")
export class Navigator extends BaseElement {
    static override styles = [styles];

    @state() protected _animName = "fadeIn";
    @state() protected _easing = "easeOutSine";
    @state() protected _playbackRate = ANIM_SPEED_MAX;
    @state() protected _directions: NavigationDirectionChangedEvent | undefined;

    @query("#left") protected _leftArrow: HTMLElement | undefined;
    @query("#right") protected _rightArrow: HTMLElement | undefined;

    public override async connectedCallback(): Promise<void> {
        super.connectedCallback();

        this._subscribeToKernelEvent(
            "ds-nav-direction-changed",
            (event: NavigationDirectionChangedEvent) => {
                this._directions = event;

                if (event.direction === "left") {
                    this._leftArrow?.classList.remove("straight", "hidden");
                    this._rightArrow?.classList.add("hidden");
                } else if (event.direction === "right") {
                    this._rightArrow?.classList.remove("straight", "hidden");
                    this._leftArrow?.classList.add("hidden");
                } else if (event.direction === "straight") {
                    this._leftArrow?.classList.remove("hidden");
                    this._leftArrow?.classList.add("straight");
                    this._rightArrow?.classList.remove("hidden");
                    this._rightArrow?.classList.add("straight");
                } else {
                    this._leftArrow?.classList.remove("hidden");
                    this._rightArrow?.classList.remove("hidden");
                }

                const color = getGradientColor(this._directions.orientationCloseness);

                if (this._leftArrow && this._rightArrow) {
                    if (this._directions.direction !== "straight") {
                        if (this._directions.orientationCloseness < 0.5) {
                            const deg = (1 - this._directions.orientationCloseness * 2) * 90;
                            this._leftArrow.style.transform = `rotate(${deg}deg)`;
                            this._rightArrow.style.transform = `rotate(-${deg}deg)`;
                        } else {
                            this._leftArrow.style.transform = "none";
                            this._rightArrow.style.transform = "none";
                        }
                    } else {
                        this._leftArrow.style.transform = "rotate(90deg)";
                        this._rightArrow.style.transform = "rotate(-90deg)";
                    }
                    this._playbackRate =
                        ANIM_SPEED_MIN +
                        (ANIM_SPEED_MAX - ANIM_SPEED_MIN) * event.orientationCloseness;

                    this._leftArrow.style.color = color;
                    this._rightArrow.style.color = color;
                }
            }
        );
    }
    override render() {
        return html`<div class="container">
            <div class="body">
                <div class="flex-row compass-container">
                    <ds-compass
                        .pointerHeading=${this._directions?.targetHeading ?? 0}
                    ></ds-compass>
                </div>
                <div class="overlay">
                    <sl-animation
                        .name=${this._animName}
                        .easing=${this._easing}
                        .playbackRate=${this._playbackRate}
                        play
                        ><sl-icon id="left" class="hidden" name="caret-left-fill"></sl-icon
                    ></sl-animation>

                    <div class="info">
                        <span class="bearing">${this._directions?.targetHeading.toFixed(0)}°</span>
                        <ds-distance
                            class="target-dist"
                            .distance=${this._directions?.distToTarget}
                            autoUnits
                        ></ds-distance>
                    </div>
                    <sl-animation
                        .name=${this._animName}
                        .easing=${this._easing}
                        .playbackRate=${this._playbackRate}
                        play
                        ><sl-icon id="right" class="hidden" name="caret-right-fill"></sl-icon
                    ></sl-animation>
                </div>
            </div>
        </div>`;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "ds-navigator": Navigator;
    }
}
